import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { topicGroups } from './topicGroups';
import './LandingPage.css';
import FeedbackModal from './FeedbackModal';
import AmplitudeTracker from './Amplitude';
import LiveMetrics from './LiveMetrics';
import PromoSlotsBadge from './PromoSlotsBadge';
import { isPrerenderCrawl } from './isPrerenderCrawl';

const LandingPage = () => {
    const topics = Object.keys(topicGroups);
    // "/" is pre-rendered by react-snap (see reactSnap.include in
    // package.json), which runs real headless Chrome and photographs
    // whatever the DOM looks like once the app's JS has run — including
    // any post-mount effect. Randomizing in a plain useEffect still gets
    // captured by the crawl (effects fire well before it captures), so the
    // snapshot ends up with one random topic baked into the Play Random
    // link's href, and every real visitor's first paint picks a *different*
    // one — an attribute mismatch that throws hydration error #418/#423 and
    // aborts hydrating the whole page, taking every other effect on it down
    // with it (LiveMetrics's heartbeat included). Skipping the randomize
    // specifically during the crawl keeps the snapshot on topics[0], which
    // is what every real client's first render also starts at — hydration
    // matches, then a real client (never headless) randomizes right after.
    const [randomTopic, setRandomTopic] = useState(topics[0]);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

    useEffect(() => {
        if (isPrerenderCrawl()) return;
        setRandomTopic(topics[Math.floor(Math.random() * topics.length)]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const popularTopics = [
        { id: 'stranger_things', name: 'Stranger Things', icon: '🚲', plays: 2091 },
        { id: 'english', name: 'English', icon: '📚', plays: 582 },
        { id: 'physics', name: 'Physics', icon: '⚛', plays: 348 },
        { id: 'mortal_kombat', name: 'Mortal Kombat', icon: '🤼', plays: 294 },
        { id: 'harry_potter', name: 'Harry Potter', icon: '🪄', plays: 254 },
        { id: 'attack_on_titan', name: 'Attack on Titan', icon: '⚔️', plays: 243 },
        { id: 'batman', name: 'Batman', icon: '🦇', plays: 239 }

    ];

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % popularTopics.length);
        }, 5000);
        return () => clearInterval(timer);
    }, [popularTopics.length]);

    // Add body class for proper styling when landing page is active
    useEffect(() => {
        document.body.classList.add('landing-page-active');
        return () => {
            document.body.classList.remove('landing-page-active');
        };
    }, []);

    const nextSlide = () => {
        setCurrentSlide((prev) => (prev + 1) % popularTopics.length);
    };

    const prevSlide = () => {
        setCurrentSlide((prev) => (prev - 1 + popularTopics.length) % popularTopics.length);
    };

    return (
        <div className="landing-page">
            <Helmet>
                <title>Topamine - Themed Wordle Games | 25+ Topics, Unlimited Play</title>
                <meta name="description" content="Play Wordle for Stranger Things, Harry Potter, Marvel, Naruto and 27 more topics. Unlimited rounds, free forever. No daily limit, no sign-up." />
                <meta name="keywords" content="themed wordle, topamine, wordle games, marvel wordle, harry potter wordle, stranger things wordle, christmas wordle, unlimited wordle, colorblind wordle, accessible word games, high contrast wordle" />
                <link rel="canonical" href="https://topamine.com/" />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://topamine.com/" />
                <meta property="og:title" content="Topamine - Themed Wordle Games | 25+ Topics" />
                <meta property="og:description" content="Play Wordle for Stranger Things, Harry Potter, Marvel, Naruto and 27 more topics. Unlimited rounds, free forever. No daily limit, no sign-up." />
                <meta property="twitter:card" content="summary" />
                <meta property="twitter:url" content="https://topamine.com/" />
                <meta property="twitter:title" content="Topamine - Themed Wordle Games | 25+ Topics" />
                <meta property="twitter:description" content="Play Wordle for Stranger Things, Harry Potter, Marvel, Naruto and 27 more topics. Unlimited rounds, free forever. No daily limit, no sign-up." />
                <script type="application/ld+json">{JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "WebSite",
                    "name": "Topamine",
                    "url": "https://topamine.com/",
                    "description": "Themed Wordle games for 25+ topics including Stranger Things, Harry Potter, Marvel, anime, sports, and more.",
                    "potentialAction": {
                        "@type": "SearchAction",
                        "target": {
                            "@type": "EntryPoint",
                            "urlTemplate": "https://topamine.com/topics?q={search_term_string}"
                        },
                        "query-input": "required name=search_term_string"
                    }
                })}</script>
            </Helmet>

            <header className="landing-header">
                <div className="header-right">
                    <a href="/advertisers" className="leaderboard-link">BID LEADERBOARD</a>
                    <PromoSlotsBadge variant="compact" />
                </div>
            </header>

            <main className="landing-main">
                <div className="hero-section">
                    <h1 className="hero-title">Finally, Wordle for the things you actually love.</h1>
                    <p className="hero-subtitle">
                        Stop waiting 24 hours for a generic word. Dive into endless word puzzles tailored to your favorite movies, shows, and topics. No daily limits, just pure play.
                    </p>

                    <div className="landing-actions">
                        <Link to={`/play/${randomTopic}`} className="play-button">
                            Play Random
                        </Link>
                        <Link to="/topics/" className="directory-button">
                            Choose a Topic
                        </Link>
                        <Link
                            to="/daily"
                            className="daily-button"
                            onClick={() => AmplitudeTracker.trackDailyChallengeCTA('home_hero', null)}
                        >
                            Daily Challenge
                        </Link>
                    </div>

                    <LiveMetrics />
                </div>

                <div className="popular-topics-preview">
                    <h2 className="section-title">Popular Topics</h2>
                    <div className="slideshow-container">
                        <button className="slide-nav prev" onClick={prevSlide} aria-label="Previous slide">
                            <ChevronLeft size={24} />
                        </button>

                        <div className="slideshow-window">
                            <div
                                className="slides-track"
                                style={{
                                    transform: `translateX(-${currentSlide * (100 / popularTopics.length)}%)`,
                                    width: `${popularTopics.length * 100}%`
                                }}
                            >
                                {popularTopics.map((topic) => (
                                    <div key={topic.id} className="slide-item">
                                        <Link to={`/topic/${topic.id}/`} className="preview-card">
                                            <span className="preview-icon">{topic.icon}</span>
                                            <h3>{topic.name}</h3>
                                            <span className="play-count">{topic.plays.toLocaleString()} plays</span>
                                        </Link>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <button className="slide-nav next" onClick={nextSlide} aria-label="Next slide">
                            <ChevronRight size={24} />
                        </button>

                        <div className="slide-dots">
                            {popularTopics.map((_, index) => (
                                <button
                                    key={index}
                                    className={`dot ${currentSlide === index ? 'active' : ''}`}
                                    onClick={() => setCurrentSlide(index)}
                                    aria-label={`Go to slide ${index + 1}`}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                <div className="features-section">
                    <h2 className="section-title">Why Topamine?</h2>
                    <div className="features-grid">
                        <div className="feature-item">
                            <h3>Unlimited Play</h3>
                            <p>No daily limits. Play as many games as you want, whenever you want. Perfect for binge-playing your favorite topics.</p>
                        </div>
                        <div className="feature-item">
                            <h3>Color Blindness Support</h3>
                            <p>Inclusive design with Pattern Mode and High Contrast Mode, ensuring everyone can enjoy the puzzle experience.</p>
                        </div>
                        <div className="feature-item">
                            <h3>Full Accessibility</h3>
                            <p>Keyboard and cursor input support make Topamine more user friendly.</p>
                        </div>
                        <div className="feature-item">
                            <h3>Growing List of Topics</h3>
                            <p>From fandoms to academic subjects, we're constantly adding new topics to keep the challenge fresh.</p>
                        </div>
                    </div>
                </div>
            </main>

            <footer className="landing-footer">
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', alignItems: 'center' }}>
                    <a href="https://startupfa.me/s/topamine-1?utm_source=topamine.com" target="_blank" rel="noreferrer">
                        <img src="https://startupfa.me/badges/featured-badge.webp" alt="Topamine - Featured on Startup Fame" width="171" height="54" />
                    </a>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', justifyContent: 'center', marginBottom: '0.5rem' }}>
                    <a href="/wordle-alternatives" className="footer-link">Wordle Alternatives</a>
                    <a href="/about" className="footer-link">About</a>
                    <a href="/privacy" className="footer-link">Privacy Policy</a>
                    <a href="/terms" className="footer-link">Terms of Service</a>
                    <a href="/advertisers" className="footer-link">Bid Leaderboard</a>
                    <a href="/blog/" className="footer-link">Blog</a>
                    <button type="button" className="footer-link footer-link-button" onClick={() => setIsFeedbackOpen(true)}>
                        Feedback
                    </button>
                    <a href="/contact" className="footer-link">Contact</a>
                </div>
                <p>&copy; {new Date().getFullYear()} Topamine. All rights reserved.</p>
            </footer>
            <FeedbackModal isOpen={isFeedbackOpen} onClose={() => setIsFeedbackOpen(false)} />
        </div>
    );
};

export default LandingPage;
